/**
 * StripeCheckoutConfigDialog — Stripe direct Checkout App 配置（ADM-PAY-S007 / A8 §9）
 *
 * 接真 `/api/v1/integrations/stripe/checkout-configs*`（useStripeCheckoutConfigs + stripeCheckoutApi）。
 *
 * 边界（A8 §9 开篇 + §9.7）：本弹窗**只**管 Account、credential route、固定回跳 URL 与启停。
 * 产品列表、价格、周期、offer 与可售状态归 Adapty；Price 路由是产品页的 `stripe_price_id`。
 * **不提供**独立 Price / 场景 slot 管理面。
 *
 * 不变量：
 * - restricted key **仅提交、永不回显**（响应连 tail / hash / 长度都没有）；首次必填，之后留空 = 沿用已存 route。
 * - 保存不激活（A8 §9.3「PUT 创建或更新 inactive 配置」）；启用要先测通再点「启用」。
 * - 回跳 URL 的 host 必须属于本 App 在本环境的回跳 host 登记（`PAYMENT_RETURN_HOSTS`）—— 前端不猜登记内容，由服务端
 *   裁决并回显错因与可填 URL。
 * - path 字面量一律取 `@novel-admin/shared` 的 `STRIPE_REDIRECT_PATHS` / `canonicalStripeRedirectPath`，
 *   本文件**不手写**：各写一份则改一侧另一侧静默失配（这正是把常量提到 shared 的理由）。
 *   两档取法与 server / 就绪度面板一致 —— **新填**（本表单）给 canonical，**既存行**的判定
 *   （下方 `redirect_path_conforms` chip）说明按**允许集合全集**，改名窗口内旧 path 不是「非法值」。
 */

import type { StripeCheckoutConfigResponse, StripeConnectionStatus } from '@novel-admin/shared';
import {
  STRIPE_CREDENTIAL_MODE_ERROR_PREFIX,
  STRIPE_REDIRECT_ALLOWLIST_ERROR_PREFIX,
  STRIPE_REDIRECT_PATHS,
  STRIPE_REDIRECT_PATH_ERROR_PREFIX,
  canonicalStripeRedirectPath,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminApiError } from '~/api';
import { type AppRow, useApps, useStripeCheckoutConfigs } from '~/data/queries';
import { stripeCheckoutApi } from '~/hooks/use-stripe-checkout';
import { detailsReasonIs } from '~/lib/details-reason';
import { Icons } from '~/lib/icons';
import { AppIcon } from '~/ui/AppIcon';
import { ConfirmDialog, Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { PasswordInput } from '~/ui/PasswordInput';
import { Select } from '~/ui/Select';
import { StatusChip } from '~/ui/StatusChip';
import { toast } from '~/ui/Toast';

const STATUS_META: Record<StripeConnectionStatus, { tone: 'pos' | 'warn' | 'neg'; label: string }> =
  {
    connected: { tone: 'pos', label: '已连接' },
    unverified: { tone: 'warn', label: '未验证' },
    invalid: { tone: 'neg', label: '失效' },
  };

function statusOf(s: string): StripeConnectionStatus {
  return s === 'connected' || s === 'invalid' ? s : 'unverified';
}

/**
 * 后端业务码 → 运营可读文案（details 里绝不含 secret，可安全展示 reason）。
 *
 * reason 比对走 `detailsReasonIs`：dd#1008 改名的部署窗口里服务端可能仍发小写，
 * 全等比较会让这三条专用文案整段退化成兜底句（见 `~/lib/details-reason` 文件头）。
 */
function describeError(e: unknown): string {
  if (!(e instanceof AdminApiError)) return '操作失败，请重试';
  const details = (e.details ?? {}) as Record<string, unknown>;
  switch (e.code) {
    case 'PAY_STRIPE_CHECKOUT_NOT_CONFIGURED':
      return detailsReasonIs(details, 'CREDENTIAL_ROUTE_MISSING')
        ? '凭据路由不存在或本环境无法解析：请重新提交 restricted key'
        : '本 App 尚未创建 Stripe Checkout 配置';
    case 'PAY_STRIPE_CHECKOUT_NOT_READY': {
      const issues = (details.issues ?? []) as { code?: string }[];
      // CREDENTIAL_STORE_UNAVAILABLE 必须单独出文案：它是**环境级**缺失，运营在本页做任何事
      // 都不会好转（重交 restricted key 也不行），必须指向运维；与「配置不完整」这类
      // 运营自己能补的项混成一句话，会让人在这里反复试。
      if (issues.some((i) => i.code === 'CREDENTIAL_STORE_UNAVAILABLE')) {
        return '当前环境未接入凭据加密载体（联系运维注入 STRIPE_CHECKOUT_KEY_ENC_KEY 与 STRIPE_MODE）——在此重新提交 restricted key 无效';
      }
      return issues.some((i) => i.code === 'REDIRECT_ALLOWLIST_MISSING')
        ? '当前环境缺少或存在无效的回跳登记，请联系运维核对本 App 的回跳配置'
        : '配置不完整：Account 或回跳 URL 缺失';
    }
    case 'PAY_STRIPE_CHECKOUT_CONNECTION_FAILED': {
      // reason 闭集二值（A8 §9.4 收窄后不再判定 Account 归属，那归 serving 侧）
      if (detailsReasonIs(details, 'PERMISSION_DENIED')) {
        return '权限不足或 Account 不匹配：restricted key 须勾选 Checkout Sessions 写、Invoices 读、Subscriptions 读写，且与所填 Account 同属一个账户（详见服务端日志）';
      }
      return '凭据无效：请在 Stripe Dashboard 重新生成 restricted key';
    }
    case 'RESOURCE_CONFLICT':
      return detailsReasonIs(details, 'OPERATION_IN_PROGRESS')
        ? '同一请求正在处理中，请稍候重试'
        : '配置在提交期间被其他人修改，请重新打开后再试';
    case 'VALIDATION_FAILED': {
      // 不能按 e.message 判：AdminApiError 构造时已用 zhErrorMessage(code) 覆盖后端 message，
      // 后端放在 message 里的 `redirect_not_allowed` 判据取不到。改按 errors[].field 判（服务端
      // 对两个回跳 URL 各挂一条 field 级错误），这是唯一被保留到前端的结构化信号。
      // restricted_key 这个 field 上有**两种**错因，必须分开说：
      //   ① mode 与本环境声明不符（A8 §9.3 不变量 5）—— key 的格式是对的，重新生成一把同 mode 的
      //      也没用，要换成本环境那一档。最典型的误操作就是把 prod live key 贴进 staging。
      //   ② shared Zod 的形态错（不是 rk_(test|live)_…）。
      // 只按 field 分支会把 ① 渲成「格式不合法」，运营会回 Dashboard 反复重生成 —— 与 #387
      // 记录的 40 分钟同一失败形态。
      const keyErrors = (e.errors ?? []).filter((x) => x.field === 'restricted_key');
      const modeErr = keyErrors.find((x) =>
        x.message.startsWith(STRIPE_CREDENTIAL_MODE_ERROR_PREFIX),
      );
      if (modeErr) {
        const expected = modeErr.message.slice(STRIPE_CREDENTIAL_MODE_ERROR_PREFIX.length);
        return `restricted key 的 mode 与本环境不符：本环境需要 ${expected}（key 格式本身没问题，重新生成同 mode 的 key 也不会通过）`;
      }
      if (keyErrors.length > 0) {
        return 'restricted key 格式不合法：需形如 rk_test_… 或 rk_live_…';
      }
      const redirectErrors = (e.errors ?? []).filter(
        (x) => x.field === 'success_url' || x.field === 'cancel_url',
      );
      if (redirectErrors.length === 0) return '输入不合法，请检查后重试';
      // 两类回跳错误的处置完全不同：path 填错要改 URL 尾巴（多半是 success/cancel 填反），
      // host 未登记要改填本 App 已登记的 host，或找运维为本 App 登记。服务端把 path 判据放在 field 级 message（顶层 message
      // 已被 zhErrorMessage 覆盖），前缀见 server 侧 REDIRECT_PATH_ERROR_PREFIX。
      const pathMismatch = redirectErrors.filter((x) =>
        x.message.startsWith(STRIPE_REDIRECT_PATH_ERROR_PREFIX),
      );
      if (pathMismatch.length > 0) {
        const expected = pathMismatch
          .map(
            (x) =>
              `${x.field === 'success_url' ? '成功' : '取消'}回跳需以 ${x.message.slice(STRIPE_REDIRECT_PATH_ERROR_PREFIX.length)} 结尾`,
          )
          .join('；');
        return `回跳 URL 路径不符：${expected}（两个字段是否填反？）`;
      }
      // 「host 未登记」必须连可填值一起给：只说「未命中」运营只能靠猜（#387 的 40 分钟即此）。
      // 可填值（本 App 登记 host + 该字段固定 path）由服务端按字段放在 field 级 message 里，前缀见 server 侧
      // REDIRECT_ALLOWLIST_ERROR_PREFIX。两个字段的可填值 path 不同，必须逐字段给（与相邻 path 分支同一写法），
      // 合成一句会让取消回跳照抄成功回跳的地址、下一次保存撞 path 不符。
      const hostHints = redirectErrors
        .filter((x) => x.message.startsWith(STRIPE_REDIRECT_ALLOWLIST_ERROR_PREFIX))
        .map(
          (x) =>
            `${x.field === 'success_url' ? '成功回跳' : '取消回跳'}可填：${x.message.slice(STRIPE_REDIRECT_ALLOWLIST_ERROR_PREFIX.length)}`,
        );
      if (hostHints.length > 0) {
        return `回跳 URL 的 host 未在本环境为该 App 登记：${hostHints.join('；')}`;
      }
      // 两个前缀都不带的 field 级错误是共享 Zod 的形态错（带 query / 空白、非 https 等），不是 host 问题
      return '回跳 URL 不合规：须为 https://<本 App 已登记的回跳域><固定路径>，不带 query / fragment';
    }
    case 'SERVICE_UNAVAILABLE':
      return 'Stripe 服务暂时不可用，请稍后重试';
    default:
      return e.message || '操作失败，请重试';
  }
}

/** 单个 App 的配置表单（新建 / 编辑合一）。 */
function StripeConfigForm({
  app,
  existing,
  appOptions,
  onCancel,
  onSaved,
}: {
  app?: AppRow;
  existing?: StripeCheckoutConfigResponse;
  appOptions: AppRow[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!existing;
  const [appId, setAppId] = useState(app?.id ?? existing?.app_id ?? appOptions[0]?.id ?? '');
  const [accountId, setAccountId] = useState(existing?.stripe_account_id ?? '');
  // 永不回填：响应里根本没有 key（连 tail 都没有）。编辑态留空 = 沿用已存 credential route。
  const [restrictedKey, setRestrictedKey] = useState('');
  const [successUrl, setSuccessUrl] = useState(existing?.success_url ?? '');
  const [cancelUrl, setCancelUrl] = useState(existing?.cancel_url ?? '');
  const [err, setErr] = useState<string | null>(null);
  const qc = useQueryClient();
  const isWeb = appOptions.find((a) => a.id === appId)?.platform === 'WEB';

  const save = useMutation({
    mutationFn: () =>
      stripeCheckoutApi.upsertConfig(
        appId,
        {
          stripe_account_id: accountId.trim(),
          // 留空 = 不换凭据（首次由服务端拒并提示 CREDENTIAL_ROUTE_MISSING）
          ...(restrictedKey.trim() ? { restricted_key: restrictedKey.trim() } : {}),
          success_url: isWeb ? null : successUrl.trim(),
          cancel_url: isWeb ? null : cancelUrl.trim(),
        },
        // 每次提交一枚新 Idempotency-Key：重试同一次提交时浏览器不会重放旧 key，
        // 真正的重放安全由服务端 operation 承担（同 key 同指纹 → 回放）。
        crypto.randomUUID(),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'stripe', 'checkout-configs'] });
      toast(isEdit ? '配置已更新（未启用）' : '配置已创建（未启用）', { tone: 'pos' });
      onSaved();
    },
    onError: (e: unknown) => setErr(describeError(e)),
  });

  const accountOk = /^acct_[A-Za-z0-9]{1,249}$/.test(accountId.trim());
  const urlsOk = [successUrl, cancelUrl].every((u) => /^https:\/\/\S+$/.test(u.trim()));
  const canSave = !!appId && accountOk && (isWeb || urlsOk) && !save.isPending;

  return (
    <div className="card-flat" style={{ padding: 14, marginBottom: 12 }}>
      <FormRow label="App" required hint="每个 App 至多一条 Stripe Checkout 配置">
        {isEdit ? (
          <span className="mono" style={{ fontSize: 12.5 }}>
            {appOptions.find((a) => a.id === appId)?.name ?? appId}
          </span>
        ) : (
          <Select
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            style={{ maxWidth: 280 }}
          >
            {appOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        )}
      </FormRow>

      <FormRow label="Stripe Account" required hint="Stripe Dashboard 的 Account ID（acct_…）">
        <input
          className="input mono"
          value={accountId}
          placeholder="acct_1234567890"
          onChange={(e) => setAccountId(e.target.value.replace(/\s/g, ''))}
          style={{ maxWidth: 360 }}
        />
        {accountId.trim() !== '' && !accountOk && (
          <div style={{ fontSize: 11, color: 'var(--neg)', marginTop: 6 }}>
            格式须为 <span className="mono">acct_</span> 开头
          </div>
        )}
      </FormRow>

      <FormRow
        label="Restricted key"
        required={!isEdit}
        hint={
          isEdit
            ? '留空 = 沿用当前凭据；填写 = 轮换（生成新的凭据路由，旧路由保留给进行中的支付）'
            : '测试环境使用 rk_test_…，正式环境使用 rk_live_… · 仅加密保存、永不回显'
        }
      >
        <PasswordInput
          value={restrictedKey}
          onChange={(e) => setRestrictedKey(e.target.value.trim())}
          placeholder={isEdit ? '留空 = 不更换' : 'rk_test_… / rk_live_…'}
        />
      </FormRow>

      {isWeb ? (
        <FormRow
          label="Web 支付回跳"
          hint="由当前环境登记的 App HTTPS origin 合成；未登记时无法测试或启用"
        >
          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Web App 不填写原生回跳 URL</span>
        </FormRow>
      ) : (
        <>
          {' '}
          <FormRow
            label="成功回跳 URL"
            required
            hint={`路径须为 ${canonicalStripeRedirectPath('success_url')}，仅域名可配；禁 query/fragment；域名须为本 App 在本环境登记的回跳域`}
          >
            <input
              className="input mono"
              value={successUrl}
              placeholder={`https://<本环境回跳域>${canonicalStripeRedirectPath('success_url')}`}
              onChange={(e) => setSuccessUrl(e.target.value.trim())}
            />
          </FormRow>
          <FormRow
            label="取消回跳 URL"
            required
            hint={`路径须为 ${canonicalStripeRedirectPath('cancel_url')}，仅域名可配；禁 query/fragment；域名须为本 App 在本环境登记的回跳域`}
          >
            <input
              className="input mono"
              value={cancelUrl}
              placeholder={`https://<本环境回跳域>${canonicalStripeRedirectPath('cancel_url')}`}
              onChange={(e) => setCancelUrl(e.target.value.trim())}
            />
          </FormRow>
        </>
      )}

      {err && (
        <div style={{ fontSize: 11.5, color: 'var(--neg)', margin: '2px 0 10px' }}>{err}</div>
      )}

      <div className="hstack" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-sm" onClick={onCancel}>
          取消
        </button>
        <button
          className="btn btn-sm btn-accent"
          disabled={!canSave}
          onClick={() => {
            setErr(null);
            save.mutate();
          }}
        >
          {save.isPending ? '保存中…' : '保存（不启用）'}
        </button>
      </div>
    </div>
  );
}

/** 已配置 App 的一行：状态 + 测试连接 + 启停 + 编辑。 */
function StripeConfigRow({
  config,
  appName,
  isWeb,
  onEdit,
}: {
  config: StripeCheckoutConfigResponse;
  appName: string;
  isWeb: boolean;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const [confirmOff, setConfirmOff] = useState(false);
  // 最近一次测试的原生一次性付款证明读取未就绪提示（A8 §9.4：后台测试界面须展示该 message）；
  // 就绪 / WEB / 测试失败时清空。就绪判定按 one_time_payment_proof_read 字段，不解析 message
  const [proofReadNotice, setProofReadNotice] = useState<string | null>(null);
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['integrations', 'stripe', 'checkout-configs'] });

  const test = useMutation({
    mutationFn: () => stripeCheckoutApi.testConnection(config.app_id),
    onSuccess: ({ data, message }) => {
      invalidate();
      const proofNotReady = data.one_time_payment_proof_read?.status === 'NOT_READY';
      setProofReadNotice(proofNotReady ? message : null);
      toast(proofNotReady ? '连接正常，可以启用（一次性账单读取未就绪）' : '连接正常，可以启用', {
        tone: proofNotReady ? 'warn' : 'pos',
      });
    },
    onError: (e: unknown) => {
      invalidate();
      setProofReadNotice(null);
      toast(describeError(e), { tone: 'neg' });
    },
  });

  const activate = useMutation({
    mutationFn: () => stripeCheckoutApi.activateConfig(config.app_id),
    onSuccess: () => {
      invalidate();
      toast('已启用 Stripe direct Checkout', { tone: 'pos' });
    },
    onError: (e: unknown) => {
      invalidate();
      toast(describeError(e), { tone: 'neg' });
    },
  });

  const deactivate = useMutation({
    mutationFn: () => stripeCheckoutApi.deactivateConfig(config.app_id),
    onSuccess: () => {
      invalidate();
      toast('已停用（进行中的已就绪支付不受影响）', { tone: 'pos' });
    },
    onError: (e: unknown) => toast(describeError(e), { tone: 'neg' }),
  });

  const meta = STATUS_META[statusOf(config.connection_status)];
  const busy = test.isPending || activate.isPending || deactivate.isPending;

  return (
    <div className="card-flat" style={{ padding: 12, marginBottom: 8 }}>
      <div className="hstack" style={{ gap: 10, marginBottom: 8 }}>
        <AppIcon label={appName.slice(0, 1).toUpperCase()} size="sm" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="hstack" style={{ gap: 6 }}>
            <span style={{ fontWeight: 500, fontSize: 13 }}>{appName}</span>
            <StatusChip tone={meta.tone} label={meta.label} />
            <span
              className={`chip ${config.is_active ? 'chip-pos' : ''}`}
              style={{ fontSize: 9.5 }}
              title={
                config.is_active
                  ? '已启用：C 端可发起 direct Checkout'
                  : '已停用：阻断新的 Checkout Session'
              }
            >
              {config.is_active ? '已启用' : '未启用'}
            </span>
            {/* 存量 active 行可能在本校验上线前把 success / cancel 填反 —— 写侧与激活侧只拦新写，
                不停用不修正就会一直按错的回跳发 Session（付完款落到取消页）。 */}
            {!isWeb && !config.redirect_path_conforms && (
              <span
                className="chip chip-neg"
                style={{ fontSize: 9.5 }}
                title={`回跳 URL 不是合法基础 URL：path 不在允许集内（成功回跳 ${STRIPE_REDIRECT_PATHS.success_url.join(
                  ' / ',
                )}；取消回跳 ${STRIPE_REDIRECT_PATHS.cancel_url.join(
                  ' / ',
                )}），或带了 query / fragment。请先停用，改正两个 URL 后重新启用（新填只接受 ${canonicalStripeRedirectPath(
                  'success_url',
                )} / ${canonicalStripeRedirectPath('cancel_url')}）。`}
              >
                回跳 path 不合规
              </span>
            )}
          </div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
            {config.stripe_account_id} · {config.credential_mode ?? '凭据路由缺失'} · v
            {config.config_version} ·{' '}
            {config.last_verified_at
              ? `最近验证 ${new Date(config.last_verified_at).toLocaleString('zh-CN')}`
              : '从未成功验证'}
            {config.deactivated_at
              ? ` · 最近停用 ${new Date(config.deactivated_at).toLocaleString('zh-CN')}`
              : ''}
          </div>
        </div>
        <button
          className="btn btn-ghost btn-icon"
          style={{ width: 26, height: 26 }}
          onClick={onEdit}
          title="编辑配置"
        >
          <Icons.IconCog size={12} />
        </button>
      </div>

      <div className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)', marginBottom: 8 }}>
        {isWeb ? (
          'Web 回跳使用当前环境登记的 App origin'
        ) : (
          <>
            ↩ {config.success_url}
            <br />✕ {config.cancel_url}
          </>
        )}
      </div>

      <div className="hstack" style={{ gap: 6 }}>
        <button className="btn btn-sm" disabled={busy} onClick={() => test.mutate()}>
          {test.isPending ? '测试中…' : '测试连接'}
        </button>
        {config.is_active ? (
          <button
            className="btn btn-sm btn-danger"
            disabled={busy}
            onClick={() => setConfirmOff(true)}
          >
            停用
          </button>
        ) : (
          <button
            className="btn btn-sm btn-accent"
            disabled={busy}
            onClick={() => activate.mutate()}
            title="启用前会实时验证凭据、Account 与权限"
          >
            {activate.isPending ? '启用中…' : '启用'}
          </button>
        )}
      </div>

      {proofReadNotice && (
        <div
          role="status"
          style={{
            marginTop: 8,
            padding: '6px 8px',
            borderRadius: 6,
            fontSize: 11,
            color: 'var(--warn)',
            background: 'var(--warn-soft)',
          }}
          title="不影响付款、恢复与启用；补齐 restricted key 的 Account / PaymentIntent / InvoicePayment / Invoice 读取权限及 webhook endpoint 版本登记后重新测试"
        >
          {proofReadNotice}
        </div>
      )}

      {confirmOff && (
        <ConfirmDialog
          title="停用 Stripe direct Checkout"
          message={`停用后 ${appName} 将不再创建新的 Checkout Session，未就绪的支付也无法恢复；已就绪的支付不受影响。`}
          confirmLabel="确认停用"
          tone="danger"
          onClose={() => setConfirmOff(false)}
          onConfirm={() => {
            setConfirmOff(false);
            deactivate.mutate();
          }}
        />
      )}
    </div>
  );
}

export function StripeCheckoutConfigDialog({ onClose }: { onClose: () => void }) {
  const { data: configs } = useStripeCheckoutConfigs();
  const { data: apps } = useApps();
  const [form, setForm] = useState<{ mode: 'add' | 'edit'; appId?: string } | null>(null);

  const appName = (id: string) => apps.find((a) => a.id === id)?.name ?? id;
  const configuredIds = new Set(configs.map((c) => c.app_id));
  const addableApps = apps.filter((a) => !configuredIds.has(a.id));
  const editing = form?.mode === 'edit' ? configs.find((c) => c.app_id === form.appId) : undefined;

  return (
    <Dialog
      width={640}
      title="Stripe direct Checkout 配置"
      subtitle="按 App 管理 Stripe 账号、凭据与回跳配置"
      icon="IconCoin"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>
            关闭
          </button>
        </>
      }
    >
      <div style={{ padding: '4px 18px 8px' }}>
        {form && (
          // key 绑定编辑目标：切换「编辑 A」→「编辑 B」时元素类型与位置不变，React 会保留旧 state，
          // 导致表单显示/提交的是上一个 App 的值。换 key 强制重挂载、重跑 useState 初值。
          <StripeConfigForm
            key={`${form.mode}:${form.appId ?? 'new'}`}
            app={apps.find((a) => a.id === form.appId)}
            existing={editing}
            appOptions={form.mode === 'add' ? addableApps : apps}
            onCancel={() => setForm(null)}
            onSaved={() => setForm(null)}
          />
        )}

        {configs.length === 0 && !form && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--fg-muted)',
              padding: '18px 0',
              textAlign: 'center',
            }}
          >
            尚未配置任何 App。新增后需先「测试连接」再「启用」。
          </div>
        )}

        {configs.map((c) => (
          <StripeConfigRow
            key={c.id}
            config={c}
            appName={appName(c.app_id)}
            isWeb={apps.find((a) => a.id === c.app_id)?.platform === 'WEB'}
            onEdit={() => setForm({ mode: 'edit', appId: c.app_id })}
          />
        ))}

        {!form && addableApps.length > 0 && (
          <button className="btn btn-sm" onClick={() => setForm({ mode: 'add' })}>
            <Icons.IconPlus size={12} />
            新增 App 配置
          </button>
        )}
      </div>
    </Dialog>
  );
}
